import { useContext, useState } from "react"
import { Link } from "react-router-dom"
import { Typography, Button, TextField } from "@mui/material"
import moment from "moment-timezone"
import { enqueueSnackbar } from 'notistack'
import _ from "lodash"

import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import AutoDeleteIcon from '@mui/icons-material/AutoDelete'
import SupportIcon from '@mui/icons-material/Support'

import { AppContext } from "/providers/app-context"
import { confirm } from '/components/confim-dialog'
import { deleteAccount, recoverAccount } from '/providers/studio-api'
import StyledPaper from '../paper.jsx'


const DeleteAccount = () => {

  const { apiSource, apiKey, client, setClient, setProcessing } = useContext(AppContext)

  const [confirmText, setConfirmText] = useState("")

  const billingDate = moment(client?.creation_date, "YYYYMMDD").date()
  const nextBillingDate = getNextBillingDate(billingDate)
  const lastUsableDate = getNextBillingDate(billingDate).subtract(1, "day")

  const finalConfirm = async () => {
    const confirmed = await confirm({
      message: 'This is you final opportunity to avoid deleting your account. Are you sure you wish to proceed to delete your account?'
    })

    if (confirmed) {
      const deletionDate = nextBillingDate.format("YYYYMMDD")

      setProcessing(true)
      enqueueSnackbar({ message: 'Marking account for deletion', variant: 'light' })
      await deleteAccount({ apiSource, apiKey, deletionDate })
      enqueueSnackbar({ message: 'Account scheduled for deletion', variant: 'success' })
      setProcessing(false)

      const c = _.clone(client)
      c.deletion_date = deletionDate
      setClient(c)
    }
  }

  const cancelDeletion = async () => {
    setProcessing(true)
    enqueueSnackbar({ message: 'Recovering account', variant: 'light' })
    await recoverAccount({ apiSource, apiKey })
    enqueueSnackbar({ message: 'Account recovered', variant: 'success' })
    setProcessing(false)

    const c = _.clone(client)
    c.deletion_date = null
    setClient(c)
  }

  if (client?.billing_mode === "postpay") {
    return (
      <div className='flex flex-col h-full items-center justify-center'>
        <StyledPaper>
          <div className='flex flex-col self-center items-center text-2xl gap-5 p-5 max-w-[900px] text-center'>
            <Typography variant="h3" color="warning.main"><WarningAmberIcon className="mr-5" sx={{ fontSize: 60 }} />Postpay account termination</Typography>

            <Typography>
              Postpay accounts cannot be terminated through this self-service page.
            </Typography>

            <Typography>
              Please raise a support ticket in <Link to='/support/history' className='link'>Support History</Link> to request termination. We will review the request, confirm any final billing requirements, and advise the termination process.
            </Typography>
          </div>
        </StyledPaper>
      </div>
    )
  }

  if (client.deletion_date) {
    const deletionDate = moment(client.deletion_date, "YYYYMMDD")

    return (
      <div className='flex flex-col h-full overflow-y-scroll'>
        <div className='flex flex-col self-center mt-20 items-center text-2xl gap-2'>
          <Typography variant="h5" color="red"><WarningAmberIcon className="mr-5" sx={{ fontSize: 60 }} />Warning! Your account is scheduled for deletion on {deletionDate.format("DD MMM YYYY")}</Typography>

          <div>You may use the button below to cancel the scheduled deletion.</div>

          <div className="flex flex-row gap-5">
            <Button onClick={cancelDeletion} variant="contained"><SupportIcon className="mr-5" />RECOVER ACCOUNT</Button>
          </div>
        </div>
      </div>
    )
  }

  else return (
    <div className='flex flex-col h-full items-center justify-center'>
      <StyledPaper>

        <div className='flex flex-col self-center items-center text-2xl gap-2 p-5'>
          <Typography variant="h3" color="red"><WarningAmberIcon className="mr-5" sx={{ fontSize: 60 }} />Warning! Deleting your account is not reversible!</Typography>

          <div >If you proceed then:</div>

          <ul className="mt-10 list-disc list-outside">
            <li className="mb-10" style={{ maxWidth: 800 }}>Your account will be remain active until the end of your current billing period ({lastUsableDate.format("DD MMM YYYY")}).
              You will be able to continue using the API, Studio and Smartix Scan until this date.
              <span className="text-xl text-blue-500"><br />(We will send a confirmation email shortly to confirm the date your account will be deleted.
                Please ensure you have downloaded any invoices that you require before this date)</span></li>

            <li className="mb-10" style={{ maxWidth: 800 }}>Any active passes will <b>expired</b> (voided) at the end of your current billing period ({lastUsableDate.format("DD MMM YYYY")}).
              At this point you will be <b>unable to access the API, Studio or use Smartix Scan</b>.
              <span className="text-xl text-blue-500"><br />(We will send a further email to confirm that your account has been deleted.)</span>
            </li>

            <li className="mb-10" style={{ maxWidth: 800 }}>You may cancel your account deletion by logging into Studio before ({lastUsableDate.format("DD MMM YYYY")}) and returning to this page and selecting the <b>cancel account deletion</b> option.</li>

            <li className="mb-10" style={{ maxWidth: 800 }}>Your account, all templates and all passes will be <b>permenantly deleted</b> from the system on {nextBillingDate.format("DD MMM YYYY")}.
              <span className="text-xl text-red-500"><br />(At which point your account will no longer be recoverable)</span></li>

            {client?.billing_mode === "postpay" ?
              <li className="mb-10" style={{ maxWidth: 800 }}>You will recive your final invoice via email on {nextBillingDate.format("DD MMM YYYY")}.</li>
              :
              <li className="mb-10" style={{ maxWidth: 800 }}>We will cancel any recurring billing that you have on  {lastUsableDate.format("DD MMM YYYY")}.</li>
            }
          </ul>

          <div>In order to proceed, please type "I wish to delete my account" below</div>
          <div className="flex flex-row gap-5">
            <TextField value={confirmText} onChange={e => setConfirmText(e.target.value)} sx={{ width: 500 }} slotProps={{ htmlInput: { style: { fontSize: 30 } } }} onPaste={e => e.preventDefault()} />
            <Button onClick={finalConfirm} disabled={confirmText.toLowerCase() !== "i wish to delete my account"} variant="contained"><AutoDeleteIcon className="mr-5" />DELETE ACCOUNT</Button>
          </div>
        </div>
      </StyledPaper>

    </div>
  )
}

export default DeleteAccount


/**
 * Finds the next date with the given day number of the month.
 * If the day is greater than the days in the month, returns the month's last day.
 * Always returns the *next* occurrence (never today).
 * 
 * @param {moment.Moment} startDate - The starting date (moment object)
 * @param {number} dayOfMonth - Target day of the month (1–31)
 * @returns {moment.Moment} The next date with the given day number
 */
function getNextBillingDate(dayOfMonth) {
  if (dayOfMonth < 1 || dayOfMonth > 31) {
    throw new Error("dayOfMonth must be between 1 and 31.");
  }

  // Start from the next day so we never return today
  let result = moment().add(1, 'day');

  // If the requested day is still ahead in this month
  if (dayOfMonth > result.date()) {
    const daysInMonth = result.daysInMonth();
    result.date(Math.min(dayOfMonth, daysInMonth));
  } else {
    // Move to next month
    result.add(1, 'month');
    const daysInMonth = result.daysInMonth();
    result.date(Math.min(dayOfMonth, daysInMonth));
  }

  return result;
}
